/**
 * Homepage copy and links, in one place.
 *
 * PLACEHOLDER COPY taken from Dan's mockup (docs/homepage-mockup.png, sent
 * Sep 1) so the page reads like the design while the client finalises
 * wording. Swap the hero, trust line and photo for the final versions before
 * launch (portal task "Collect launch inputs from Dan/Branden"). Links point
 * at pages that exist today; see docs/homepage-option-a.md for the map.
 */

export const ANNOUNCEMENT = {
  enabled: true,
  text: 'Get hired through the Safety Knights Job Board and receive $100.',
  linkText: 'Learn more',
  link: ['/jobs'],
  queryParams: { view: 'all' },
};

export const HERO = {
  headlineLine1: 'Less busy work.',
  headlineLine2: 'More impact.',
  subhead: 'Safety Knights helps safety professionals save time, find trusted solutions, earn free CE, and grow their careers, all in one place.',
  primaryCta: 'Join Free',
  secondaryCta: 'Explore What We Do',
  trustLine: '100% free for safety professionals. Built for the work you do every day.',
  // Placeholder photo from the existing site assets until the client sends one.
  image: 'static/community.jpg',
  imageAlt: 'Safety professional in a hard hat and hi-vis vest reviewing a tablet',
};

export interface HomeCard { icon: string; tone: string; title: string; body: string; linkText: string; link: string[]; }

// 5. "What can Safety Knights make easier for you?"
export const CARDS_HEADING = { before: 'What can Safety Knights make ', accent: 'easier', after: ' for you?' };
export const CARDS_SUBHEAD = 'Four ways we take things off your plate, so you can focus on what matters most.';
export const CARDS: HomeCard[] = [
  { icon: 'fa-solid fa-briefcase', tone: 'green', title: 'Find Your Next Safety Job', body: 'Opportunities for EHS and safety professionals, all in one place.', linkText: 'Explore Jobs', link: ['/jobs'] },
  { icon: 'fa-solid fa-graduation-cap', tone: 'orange', title: 'Earn Free Continuing Education', body: 'Accredited webinars and summits that fit your schedule, no cost.', linkText: 'View Upcoming Events', link: ['/content', 'event-webinar'] },
  { icon: 'fa-solid fa-handshake', tone: 'teal', title: 'Find a Solution You Can Trust', body: 'Vetted partners in every key category, so you do not waste time or guess.', linkText: 'Explore Solutions', link: ['/partners'] },
  { icon: 'fa-solid fa-user-group', tone: 'purple', title: 'Learn from People Who Do the Work', body: 'Practical webinars, sessions, and insights from real practitioners.', linkText: 'Explore Education', link: ['/content'] },
];

// 6. Partner strip (left panel next to the logos)
export const PARTNER_CTA = {
  heading: 'Prefer to partner with Safety Knights?',
  body: 'Get in front of 70,000+ safety professionals and grow your impact.',
  cta: 'Learn about partnership opportunities',
  link: ['/partners'],
};

// 11. Newsletter
export const NEWSLETTER = {
  heading: 'Stay informed without living in your inbox.',
  // "every Monday" is from the mockup; Dan is confirming the send day.
  body: 'Practical safety news, resources, jobs and upcoming education delivered every Monday.',
  cta: 'Join the Newsletter',
  successTitle: 'You are on the list',
  successBody: 'Look for the next issue in your inbox.',
};

// 12. Footer
export interface FooterLink { label: string; link?: string[]; href?: string; queryParams?: Record<string, string>; }
export interface FooterColumn { heading: string; links: FooterLink[]; }
export const FOOTER_TAGLINE = 'Helping safety professionals do the job better.';
export const FOOTER_COLUMNS: FooterColumn[] = [
  { heading: 'Jobs', links: [
    { label: 'Browse Jobs', link: ['/jobs'] },
    { label: 'Career Resources', link: ['/jobs'] },
    { label: 'For Employers', link: ['/jobs'], queryParams: { view: 'all' } },
  ] },
  { heading: 'Resources', links: [
    { label: 'Articles & Guides', link: ['/content', 'article'] },
    { label: 'Templates', link: ['/content', 'written-program'] },
    { label: 'Checklists', link: ['/content', 'written-program'] },
    { label: 'Tools', link: ['/content'] },
  ] },
  { heading: 'Education & Events', links: [
    { label: 'Upcoming Events', link: ['/content', 'event-webinar'] },
    { label: 'Webinars & Recordings', link: ['/content', 'videos'] },
    { label: 'Continuing Education', link: ['/content'] },
    { label: 'Summits', link: ['/peoples-safety-summit'] },
  ] },
  { heading: 'Community', links: [
    { label: 'Discussions', link: ['/community'] },
    { label: 'Ask a Question', link: ['/community'] },
    { label: 'Member Directory', link: ['/search'] },
  ] },
  { heading: 'Find Solutions', links: [
    { label: 'Preferred Partners', link: ['/partners'] },
    { label: 'By Category', link: ['/partners'] },
    { label: 'Become a Partner', link: ['/partners'] },
  ] },
  { heading: 'About', links: [
    { label: 'About Us', link: ['/mission'] },
    { label: 'Contact Us', link: ['/contact'] },
    { label: 'Sponsor', link: ['/sponsors'] },
    { label: 'Advertise', link: ['/sponsors'] },
  ] },
];
export const FOOTER_PARTNER_BOX = {
  heading: 'Partner With Us',
  body: 'Get in front of 70,000+ safety professionals and grow your impact.',
  cta: 'Learn more',
  link: ['/partners'],
};
export const FOOTER_LEGAL: FooterLink[] = [
  { label: 'Terms of Use', link: ['/terms'] },
  { label: 'Privacy Policy', link: ['/privacy'] },
  { label: 'Code of Conduct', link: ['/guidelines'] },
];
export const FOOTER_SOCIAL = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/safety-knights', icon: 'fa-brands fa-linkedin-in' },
  { label: 'Facebook', href: 'https://www.facebook.com/safetyknights', icon: 'fa-brands fa-facebook-f' },
  { label: 'X', href: 'https://x.com/SafetyKnights', icon: 'fa-brands fa-x-twitter' },
  { label: 'Instagram', href: 'https://www.instagram.com/safety_knights/', icon: 'fa-brands fa-instagram' },
];
